import { micromark } from "micromark";
import { gfm, gfmHtml } from "micromark-extension-gfm";
import { HtmlExtension } from "micromark-util-types";
import { NodeHtmlMarkdown, TranslatorConfigObject } from "node-html-markdown";

import {
  emptyLinesHtmlExtension,
  emptyLinesSyntaxExtension,
} from "src/base-components/TextEditor/emptyLinesExtension";

const DEFAULT_TRANSLATORS: TranslatorConfigObject = {
  pre: {
    prefix: "\n```\n",
    postfix: "```",
    noEscape: true,
  },
};

const KEEP_EMPTY_LINES_TRANSLATORS: TranslatorConfigObject = {
  ...DEFAULT_TRANSLATORS,
  br: {
    content: "&nbsp;",
    recurse: false,
  },
};

const gfmHtmlExtensions: HtmlExtension = {
  ...gfmHtml(),
  enter: {
    ...gfmHtml().enter,
    strikethrough() {
      this.tag("<s>");
    },
  },
  exit: {
    ...gfmHtml().exit,
    strikethrough() {
      this.tag("</s>");
    },
  },
};

type ToMarkdownOptions = {
  keepEmptyLines?: boolean;
};

// Quill v2's getSemanticHTML emits empty blocks as `<p></p>` / `<h2></h2>` rather
// than `<p><br></p>`. Without the inner `<br>`, node-html-markdown drops the
// block entirely, so empty paragraphs round-trip away. Inject a `<br>` so the
// `br` translator (see KEEP_EMPTY_LINES_TRANSLATORS) emits `&nbsp;`.
const EMPTY_BLOCK_RE = /<(p|h[1-6]|li|blockquote)(\s[^>]*)?>\s*<\/\1>/g;
const normalizeEmptyBlocks = (html: string) =>
  html.replace(EMPTY_BLOCK_RE, "<$1$2><br></$1>");

/**
 * @internal Internal to TextEditor — use MarkdownRenderer for rendering markdown elsewhere.
 */
export const mdService = {
  toMarkdown: (html: string, options: ToMarkdownOptions = {}) => {
    const input = options.keepEmptyLines ? normalizeEmptyBlocks(html) : html;
    return NodeHtmlMarkdown.translate(
      input,
      {},
      options.keepEmptyLines
        ? KEEP_EMPTY_LINES_TRANSLATORS
        : DEFAULT_TRANSLATORS,
    ).trim();
  },
  toHtml: (markdown: string) =>
    micromark(markdown, {
      extensions: [gfm(), emptyLinesSyntaxExtension],
      htmlExtensions: [gfmHtmlExtensions, emptyLinesHtmlExtension],
    }).trim(),
};
